import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  NavLink,
  Outlet,
  useOutletContext,
} from "react-router-dom";
import { AlertTriangle, Settings as SettingsIcon, LogOut, LayoutDashboard, Radar } from "lucide-react";
import { CompetitorAnalysis } from "@/components/CompetitorAnalysis";
import { Dashboard } from "@/components/Dashboard";
import { CompetitorDetail } from "@/components/CompetitorDetail";
import { ForgotPassword } from "@/components/ForgotPassword";
import { SignIn } from "@/components/SignIn";
import { SignUp } from "@/components/SignUp";
import { VerifyEmail } from "@/components/VerifyEmail";
import { OnboardingWizard } from "@/components/OnboardingWizard";
import { Settings } from "@/components/Settings";
import { LandingHero } from "@/components/LandingHero";
import { SiteFooter } from "@/components/SiteFooter";
import { GuidesPage } from "@/components/marketing/GuidesPage";
import { PlaybooksPage } from "@/components/marketing/PlaybooksPage";
import { StatusPage } from "@/components/marketing/StatusPage";
import { Wordmark } from "@/components/brand";
import { InteractiveBackdrop } from "@/components/InteractiveBackdrop";
import { useAuth } from "@/lib/auth";
import { getProfile, isOnboardingComplete, loadProfile, NovaSynthProfile, persistProfile } from "@/lib/profile";

function getOrCreateSessionId(): string {
  const KEY = "novasynth_session_id";
  const existing = sessionStorage.getItem(KEY);
  if (existing && existing.length >= 33) return existing;
  const id = crypto.randomUUID();
  sessionStorage.setItem(KEY, id);
  return id;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class AppErrorBoundary extends React.Component<
  React.PropsWithChildren<Record<string, unknown>>,
  ErrorBoundaryState
> {
  constructor(props: React.PropsWithChildren<Record<string, unknown>>) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[NovaSynth] Unhandled error:", error, info.componentStack);
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div className="relative min-h-screen flex items-center justify-center px-4 overflow-hidden">
          <InteractiveBackdrop />
          <div className="relative z-10 max-w-md w-full ns-glass rounded-2xl p-8 text-center shadow-card-lg">
            <div className="w-12 h-12 bg-red-950/40 border border-red-800/40 rounded-xl flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-6 h-6 text-red-400" />
            </div>
            <h2 className="text-base font-semibold text-red-300 mb-2">Something went wrong</h2>
            <p className="text-sm text-slate-400 mb-1">{this.state.error?.message ?? "An unexpected error occurred."}</p>
            <p className="text-xs text-slate-600 mb-6">Please try again - your data is safe.</p>
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              className="px-4 py-2.5 bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-500 hover:to-brand-400 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer min-h-[44px]"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Authed app context (shared with routed pages via <Outlet context>) ────────

interface AppContext {
  idToken: string;
  sessionId: string;
  profile: NovaSynthProfile | null;
  onProfileSaved: (p: NovaSynthProfile) => void;
  onAuthError: () => void;
  /** Pending competitor to auto-launch on the analyze route (consumed once). */
  pendingCompetitor: string | undefined;
  consumePendingCompetitor: () => void;
  /** Navigate to the analyze route and launch a competitor. */
  analyze: (competitor: string) => void;
}

function useAppContext() {
  return useOutletContext<AppContext>();
}

export function App() {
  const auth = useAuth();
  const {
    accessToken,
    idToken,
    isLoading,
    isAuthenticated,
    needsVerification,
    needsPasswordReset,
    pendingEmail,
    error,
    login,
    logout,
    clearError,
    register,
    verify,
    resendCode,
    forgotPassword,
    confirmForgotPassword,
  } = auth;
  const sessionId = useMemo(() => getOrCreateSessionId(), []);
  const navigate = useNavigate();
  const location = useLocation();

  const [onboardingDone, setOnboardingDone] = useState(() => isOnboardingComplete());
  const [profileChecked, setProfileChecked] = useState(false);
  const [profile, setProfile] = useState<NovaSynthProfile | null>(() => getProfile());

  // Pending competitor to auto-launch on the analyze route (from onboarding,
  // landing, watchlist "Run again", or competitor detail).
  const [pendingCompetitor, setPendingCompetitor] = useState<string | undefined>();

  const handleAuthError = useCallback(() => { logout(); }, [logout]);

  // Reconcile onboarding/profile with the account-tied server store on login.
  useEffect(() => {
    if (!isAuthenticated) {
      setProfileChecked(false);
      return;
    }
    if (!idToken || profileChecked) return;
    let cancelled = false;
    (async () => {
      const res = await loadProfile(idToken);
      if (!cancelled) {
        setOnboardingDone(res.onboarded);
        if (res.profile) setProfile(res.profile);
        setProfileChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, idToken, profileChecked]);

  function handleOnboardingComplete(competitor?: string) {
    setOnboardingDone(true);
    const p = getProfile();
    if (p) setProfile(p);
    if (p && idToken) void persistProfile(idToken, p);
    if (competitor) {
      setPendingCompetitor(competitor);
      navigate("/app/analyze");
    } else {
      navigate("/app");
    }
  }

  // Analyze helper used across authed pages - sets the pending competitor and
  // routes to the analyze view (which auto-starts).
  const analyze = useCallback((competitor: string) => {
    setPendingCompetitor(competitor);
    navigate("/app/analyze");
  }, [navigate]);

  if (isLoading) {
    return <BrandLoader label="Starting NovaSynth" />;
  }

  // ── Email verification after sign-up (modal-free full screen, keep logic) ──
  if (needsVerification && pendingEmail) {
    return (
      <VerifyEmail
        email={pendingEmail}
        onVerify={verify}
        onResend={resendCode}
        onBack={() => { clearError(); navigate("/signin"); }}
        isLoading={isLoading}
        error={error}
        onClearError={clearError}
      />
    );
  }

  // ── Password reset flow (forgot password) ──
  if (needsPasswordReset) {
    return (
      <ForgotPassword
        onForgotPassword={forgotPassword}
        onConfirmForgotPassword={confirmForgotPassword}
        needsPasswordReset={needsPasswordReset}
        pendingEmail={pendingEmail}
        onBack={() => { clearError(); navigate("/signin"); }}
        isLoading={isLoading}
        error={error}
        onClearError={clearError}
      />
    );
  }

  const authed = isAuthenticated && Boolean(accessToken);

  // ── Unauthenticated: public landing + auth routes ──
  if (!authed) {
    return (
      <Routes>
        <Route path="/" element={<PublicLanding />} />
        <Route
          path="/signin"
          element={
            <SignIn
              onSignIn={login}
              onSwitchToSignUp={() => { clearError(); navigate("/signup"); }}
              onSwitchToForgot={() => { clearError(); navigate("/forgot"); }}
              isLoading={isLoading}
              error={error}
              onClearError={clearError}
            />
          }
        />
        <Route
          path="/signup"
          element={
            <SignUp
              onSignUp={register}
              onSwitchToSignIn={() => { clearError(); navigate("/signin"); }}
              isLoading={isLoading}
              error={error}
              onClearError={clearError}
            />
          }
        />
        <Route
          path="/forgot"
          element={
            <ForgotPassword
              onForgotPassword={forgotPassword}
              onConfirmForgotPassword={confirmForgotPassword}
              needsPasswordReset={needsPasswordReset}
              pendingEmail={pendingEmail}
              onBack={() => { clearError(); navigate("/signin"); }}
              isLoading={isLoading}
              error={error}
              onClearError={clearError}
            />
          }
        />
        {/* Public resource pages - render whether or not the user is signed in.
            MUST sit above the catch-all or they would redirect to the landing. */}
        <Route path="/guides" element={<GuidesPage authed={false} />} />
        <Route path="/playbooks" element={<PlaybooksPage authed={false} />} />
        <Route path="/status" element={<StatusPage authed={false} />} />
        {/* Any other path while logged out → landing. */}
        <Route path="*" element={<Navigate to="/" replace state={{ from: location.pathname }} />} />
      </Routes>
    );
  }

  // ── Authenticated but profile not reconciled yet → loader (no wizard flash) ──
  if (!profileChecked) {
    return <BrandLoader label="Loading your workspace" />;
  }

  // ── Authenticated but onboarding incomplete → wizard ──
  if (!onboardingDone) {
    return <OnboardingWizard idToken={idToken ?? ""} onComplete={handleOnboardingComplete} />;
  }

  // ── Authenticated app shell ──
  const ctx: AppContext = {
    idToken: idToken ?? "",
    sessionId,
    profile,
    onProfileSaved: setProfile,
    onAuthError: handleAuthError,
    pendingCompetitor,
    consumePendingCompetitor: () => setPendingCompetitor(undefined),
    analyze,
  };

  return (
    <AppErrorBoundary>
      <Routes>
        <Route element={<AppShell onSignOut={logout} ctx={ctx} />}>
          <Route path="/app" element={<DashboardPage />} />
          <Route path="/app/analyze" element={<AnalyzePage />} />
          <Route path="/app/competitor/:name" element={<CompetitorDetailPage />} />
        </Route>
        {/* Public resource pages are reachable while signed in too (e.g. from the
            app footer). They render their own marketing chrome, not the app shell,
            and MUST sit above the catch-all or they would redirect to /app. */}
        <Route path="/guides" element={<GuidesPage authed />} />
        <Route path="/playbooks" element={<PlaybooksPage authed />} />
        <Route path="/status" element={<StatusPage authed />} />
        {/* Logged-in users never see the public landing or auth screens. */}
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>
    </AppErrorBoundary>
  );
}

// ── Public marketing landing (logged-out) ─────────────────────────────────────

function PublicLanding() {
  const navigate = useNavigate();
  // Logged-out launch → take the user to sign up (no profile/tracked chips yet).
  const onLaunch = useCallback(() => { navigate("/signup"); }, [navigate]);
  return (
    <div className="relative min-h-screen flex flex-col overflow-hidden">
      <InteractiveBackdrop />
      <header className="sticky top-0 z-30 border-b border-[#1a2640]/80 bg-[#020617]/80 backdrop-blur-xl">
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-brand-600/25 to-transparent" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Wordmark size="md" showTagline />
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/signin")}
              className="px-3.5 py-2 text-xs font-medium text-slate-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors cursor-pointer min-h-[40px]"
            >
              Sign in
            </button>
            <button
              onClick={() => navigate("/signup")}
              className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-brand-600 to-brand-500 px-3.5 py-2 text-xs font-semibold text-white shadow-glow-brand-sm transition-all hover:from-brand-500 hover:to-brand-400 cursor-pointer min-h-[40px]"
            >
              Get started
            </button>
          </div>
        </div>
      </header>
      <main className="relative z-10 flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 py-8">
        <LandingHero trackedCompetitors={[]} onLaunch={onLaunch} />
      </main>
    </div>
  );
}

// ── Authed app shell (header nav + InteractiveBackdrop + footer + Outlet) ─────

const NAV: { to: string; label: string; Icon: typeof LayoutDashboard; end?: boolean }[] = [
  { to: "/app", label: "Watchlist", Icon: LayoutDashboard, end: true },
  { to: "/app/analyze", label: "Analyze", Icon: Radar },
];

function AppShell({ onSignOut, ctx }: { onSignOut: () => void; ctx: AppContext }) {
  const [showSettings, setShowSettings] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="relative min-h-screen flex flex-col overflow-x-hidden">
      <InteractiveBackdrop />

      <header data-print="hide" className="sticky top-0 z-30 border-b border-[#1a2640]/80 bg-[#020617]/80 backdrop-blur-xl">
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-brand-600/25 to-transparent" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-5 min-w-0">
            <NavLink to="/app" className="cursor-pointer shrink-0">
              <Wordmark size="md" showTagline />
            </NavLink>
            <nav className="hidden sm:flex items-center gap-1">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition-colors cursor-pointer min-h-[40px] ${
                      isActive
                        ? "text-[#22d3ee] bg-[#22d3ee]/10 border border-[#22d3ee]/25"
                        : "text-slate-400 hover:text-slate-100 hover:bg-white/5 border border-transparent"
                    }`
                  }
                >
                  <item.Icon className="w-3.5 h-3.5" />
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-1.5">
            <span className="hidden md:flex items-center gap-1.5 mr-2 rounded-full border border-emerald-700/30 bg-emerald-500/5 px-2.5 py-1 text-[11px] font-medium text-emerald-300">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              Secure session
            </span>
            <button
              onClick={() => setShowSettings(true)}
              className="p-2.5 text-slate-400 hover:text-slate-100 hover:bg-white/5 rounded-lg transition-colors cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center"
              title="Settings"
              aria-label="Settings"
            >
              <SettingsIcon className="w-4 h-4" />
            </button>
            <div className="w-px h-5 bg-[#1a2640] mx-0.5" />
            <button
              onClick={onSignOut}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-400 hover:text-slate-100 hover:bg-white/5 rounded-lg transition-colors border border-transparent hover:border-[#1a2640] cursor-pointer min-h-[44px]"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>

        {/* Mobile nav row */}
        <nav className="sm:hidden flex items-center gap-1 px-4 pb-2 -mt-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition-colors cursor-pointer ${
                  isActive ? "text-[#22d3ee] bg-[#22d3ee]/10 border border-[#22d3ee]/25" : "text-slate-400 hover:text-slate-100 hover:bg-white/5 border border-transparent"
                }`
              }
            >
              <item.Icon className="w-3.5 h-3.5" />
              {item.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="relative flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 py-10 z-10">
        <Outlet context={ctx} />
      </main>

      <SiteFooter variant="app" onWatchlist={() => navigate("/app")} onAnalyze={() => navigate("/app/analyze")} />

      {showSettings && (
        <Settings
          idToken={ctx.idToken}
          profile={ctx.profile}
          onClose={() => setShowSettings(false)}
          onSaved={(p) => { ctx.onProfileSaved(p); setShowSettings(false); }}
        />
      )}
    </div>
  );
}

// ── Routed pages (read shared context) ────────────────────────────────────────

function DashboardPage() {
  const ctx = useAppContext();
  return <Dashboard idToken={ctx.idToken} profile={ctx.profile} onAnalyze={ctx.analyze} />;
}

function AnalyzePage() {
  const ctx = useAppContext();
  return (
    <CompetitorAnalysis
      idToken={ctx.idToken}
      sessionId={ctx.sessionId}
      onAuthError={ctx.onAuthError}
      initialCompetitor={ctx.pendingCompetitor}
      onInitialCompetitorConsumed={ctx.consumePendingCompetitor}
      profile={ctx.profile}
    />
  );
}

function CompetitorDetailPage() {
  const ctx = useAppContext();
  return <CompetitorDetail idToken={ctx.idToken} onAnalyze={ctx.analyze} />;
}

/** Branded full-screen loader used during auth + workspace hydration. */
function BrandLoader({ label }: { label: string }) {
  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <InteractiveBackdrop />
      <div className="relative z-10 flex flex-col items-center gap-4">
        <div className="relative w-12 h-12">
          <div className="absolute inset-0 rounded-2xl border border-brand-700/30" />
          <div className="absolute inset-0 rounded-2xl border border-transparent border-t-[#22d3ee] animate-spin" />
          <span className="absolute inset-0 flex items-center justify-center">
            <Wordmark size="sm" />
          </span>
        </div>
        <p className="font-mono text-xs text-slate-500 tracking-[0.18em] uppercase">{label}</p>
      </div>
    </div>
  );
}
